import { Button } from "@material-ui/core";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Popover from "@material-ui/core/Popover";
import FilterListIcon from "@material-ui/icons/FilterList";
import React, { FC, useEffect, useState } from "react";
import { useListContext } from "react-admin";

interface StatusOption {
  id: string;
  label: string;
}

interface StatusPopoverProps {
  options: StatusOption[];
  filterKey?: string;
  label?: string;
}

const StatusPopover: FC<StatusPopoverProps> = ({
  options,
  filterKey = "status",
  label = "Status",
}) => {
  const { filterValues, setFilters } = useListContext();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [localSelected, setLocalSelected] = useState<string[]>([]);
  const [pendingFilters, setPendingFilters] = useState<Record<
    string,
    any
  > | null>(null);

  const fv = filterValues as Record<string, any>;
  const currentValue = fv[filterKey] || "";

  // Sync local state from filterValues when popover opens
  useEffect(() => {
    if (anchorEl) {
      setLocalSelected(currentValue ? currentValue.split(",") : []);
    }
  }, [anchorEl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply pending filters AFTER popover DOM is removed
  useEffect(() => {
    if (!anchorEl && pendingFilters) {
      setFilters(pendingFilters, undefined);
      setPendingFilters(null);
    }
  }, [anchorEl, pendingFilters, setFilters]);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = (id: string) => {
    setLocalSelected((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
  };

  const handleApply = () => {
    const newFilters = { ...filterValues };
    delete (newFilters as any)[filterKey];
    if (localSelected.length > 0) {
      (newFilters as any)[filterKey] = localSelected.join(",");
    }
    setPendingFilters(newFilters);
    handleClose();
  };

  const handleClear = () => {
    const newFilters = { ...filterValues };
    delete (newFilters as any)[filterKey];
    setPendingFilters(newFilters);
    setLocalSelected([]);
    handleClose();
  };

  const hasFilter = !!currentValue;

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={handleOpen}
        startIcon={<FilterListIcon fontSize="small" />}
        style={{
          textTransform: "none",
          minWidth: "auto",
          padding: "4px 10px",
          fontSize: "0.8125rem",
          borderColor: hasFilter ? "rgba(0, 0, 0, 0.12)" : undefined,
          backgroundColor: hasFilter ? "rgba(0, 0, 0, 0.08)" : undefined,
        }}
      >
        {label}
      </Button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <div
          style={{
            padding: "0.5rem 1rem 1rem 1rem",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="subtitle2">Status</Typography>
          </Box> */}
          <div
            style={{
              padding: "0.25rem 0rem 0.5rem 0rem",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {options.map((opt) => (
              <FormControlLabel
                key={opt.id}
                control={
                  <Checkbox
                    checked={localSelected.includes(opt.id)}
                    onChange={() => handleToggle(opt.id)}
                    color="primary"
                    size="small"
                    style={{
                      paddingTop: "4px",
                      paddingBottom: "4px",
                    }}
                  />
                }
                label={opt.label}
              />
            ))}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              paddingTop: 8,
            }}
          >
            <Button size="small" onClick={handleClear}>
              Clear
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={handleApply}
              disabled={localSelected.length === 0}
            >
              Apply
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
};

export default StatusPopover;
